import { useState, useEffect } from 'react';
import { GameSession, PlayerWithUsername } from '../types';
import apiService from '../services/api';
import useAuthStore from '../stores/authStore';

export interface UseActiveAdventuresOptions {
  /**
   * When true, also load each session's interpolated current scene.
   * Active-Adventure-List does not need this; fetching it hydrates a game
   * engine per session and delays the list.
   */
  loadSceneDetails?: boolean;
}

export interface ActiveAdventure {
  session: GameSession;
  characterName?: string;
  currentSceneDescription?: string;
  playerId?: string;
}

export interface UseActiveAdventuresResult {
  adventures: ActiveAdventure[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

type SharedLoad = {
  key: string;
  promise: Promise<ActiveAdventure[]>;
};

let sharedActiveAdventureLoad: SharedLoad | null = null;

function getCharacterName(player: PlayerWithUsername): string | null {
  return player.characterName && player.characterName.trim() !== ''
    ? player.characterName
    : null;
}

function findCurrentUserPlayer(
  players: PlayerWithUsername[],
  userId: string | undefined
): PlayerWithUsername | undefined {
  const matching = players.filter((player) => player.userId === userId);
  return matching.find((player) => !player.completed) ?? matching[0];
}

async function loadActiveAdventures(loadSceneDetails: boolean): Promise<ActiveAdventure[]> {
  const sessions = await apiService.getActiveSessions();
  const currentUser = useAuthStore.getState().user;

  const adventuresWithDetails = await Promise.all(
    sessions.map(async (session) => {
      try {
        // Active-sessions already includes players. Only refetch when missing.
        let players = session.players;
        if (!players || players.length === 0) {
          players = await apiService.getSessionPlayers(session.id);
        }

        const currentUserPlayer = findCurrentUserPlayer(players, currentUser?.id);
        if (!currentUserPlayer) {
          return null;
        }

        if (!loadSceneDetails) {
          return {
            session,
            characterName: getCharacterName(currentUserPlayer),
            currentSceneDescription: 'Continue your adventure...',
            playerId: currentUserPlayer.id
          };
        }

        try {
          const currentScene = await apiService.getCurrentScene(session.id, currentUserPlayer.id);
          const description = currentScene.scene?.sections?.[0]?.description
            ? (typeof currentScene.scene.sections[0].description === 'string'
                ? currentScene.scene.sections[0].description
                : 'Scene description')
            : `Scene: ${currentScene.scene?.id || 'Unknown'}`;

          return {
            session,
            characterName: getCharacterName(currentUserPlayer),
            currentSceneDescription: description,
            playerId: currentUserPlayer.id
          };
        } catch (sceneError) {
          console.warn(`useActiveAdventures: Failed to load scene details for session ${session.id}:`, sceneError);
          return {
            session,
            characterName: getCharacterName(currentUserPlayer),
            currentSceneDescription: 'Continue your adventure...',
            playerId: currentUserPlayer.id
          };
        }
      } catch (error) {
        console.error(`useActiveAdventures: Error processing session ${session.id}:`, error);
        return null;
      }
    })
  );

  return adventuresWithDetails.filter((adventure) => adventure !== null) as ActiveAdventure[];
}

function loadActiveAdventuresShared(
  key: string,
  loadSceneDetails: boolean
): Promise<ActiveAdventure[]> {
  if (sharedActiveAdventureLoad?.key === key) {
    return sharedActiveAdventureLoad.promise;
  }

  const promise = loadActiveAdventures(loadSceneDetails).finally(() => {
    if (sharedActiveAdventureLoad?.promise === promise) {
      sharedActiveAdventureLoad = null;
    }
  });
  sharedActiveAdventureLoad = { key, promise };
  return promise;
}

export const useActiveAdventures = (
  options?: UseActiveAdventuresOptions
): UseActiveAdventuresResult => {
  const loadSceneDetails = options?.loadSceneDetails ?? false;
  const [adventures, setAdventures] = useState<ActiveAdventure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActiveAdventures = async () => {
    // Check if user is authenticated first (and not a guest)
    const authState = useAuthStore.getState();
    const currentUser = authState.user;
    
    // Guest users don't have active adventures - they're just starting
    // Skip API calls for guest users to avoid "Access token is required" errors
    if (!currentUser || authState.isGuest) {
      setAdventures([]);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Share one in-flight load when several lists mount on the same scene.
      const validAdventures = await loadActiveAdventuresShared(
        `${currentUser.id}:${loadSceneDetails ? 'scenes' : 'summary'}`,
        loadSceneDetails
      );

      setAdventures(validAdventures);
    } catch (err) {
      console.error('useActiveAdventures: Error occurred:', err);
      
      // Show errors for API issues, but gracefully handle missing endpoint
      const errorMessage = err instanceof Error ? err.message : 'Failed to load active adventures';
      
      // If it's a 404 or the endpoint doesn't exist, just return empty array
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        setAdventures([]);
        setError(null); // Don't show error to user for missing backend feature
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveAdventures();
  }, [loadSceneDetails]);

  return {
    adventures,
    loading,
    error,
    refetch: fetchActiveAdventures
  };
};
